import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';
import { FaRedo } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { ContentSection } from './Layout/ContentSection';
import { CustomTable } from './Table';
import ClassicLoadingSpinner from './LoadingSpinner/ClassicLoadingSpinner';
import ContainerTypeDisplay from './ContainerType/ContainerTypeDisplay';
import CustomPagination from './CustomPagination';
import { useGetCurrentStockForContainerStationQuery } from '../graphql/queries/getCurrentStockForContainerStation.generated';

const INDIVIDUAL_CONTAINER_STOCK_PAGE_SIZE = 50;

type IndividualContainerStockProps = {
  stationId: string;
};

// Lists the individual containers currently booked to a container station
// (the open location rows, NextOwnerId IS NULL). Backed by
// findCurrentStockForContainerStationPaginated, which hits the partial index
// on (OwnerId, ContainerStationId) and stays fast regardless of history size.
const IndividualContainerStock = ({
  stationId,
}: IndividualContainerStockProps) => {
  const [page, setPage] = useState(1);

  const { data, previousData, loading, error, refetch } =
    useGetCurrentStockForContainerStationQuery({
      skip: stationId == null || stationId === '',
      variables: {
        container_station_id: stationId,
        skip: (page - 1) * INDIVIDUAL_CONTAINER_STOCK_PAGE_SIZE,
        take: INDIVIDUAL_CONTAINER_STOCK_PAGE_SIZE,
      },
    });

  // Fall back to previousData while a new page is loading. With the default
  // cache-first policy, changing `page` makes `data` undefined mid-fetch; using
  // previousData keeps `total`/`pageCount` stable so the pagination stays
  // mounted (no flicker / reset) across navigation.
  const result =
    data?.findCurrentStockForContainerStationPaginated ??
    previousData?.findCurrentStockForContainerStationPaginated;
  const containers = result?.containers ?? [];
  const total = result?.total ?? 0;
  const pageCount = Math.ceil(total / INDIVIDUAL_CONTAINER_STOCK_PAGE_SIZE);

  const headers = [
    i18n.t('Common:name'),
    i18n.t('ContainerStation:containerType'),
    i18n.t('ContainerStation:status'),
    i18n.t('ContainerStation:ownerSince'),
  ];

  return (
    <ContentSection
      title={i18n.t('ContainerStation:individualContainerStock')}
      collapsable={false}
    >
      <div className="float-end mb-2">
        <span className="me-2">
          {i18n.t('ContainerStation:containers')}: {total}
        </span>
        <Button
          className="mb-1 ms-1"
          disabled={loading}
          onClick={() => refetch()}
        >
          <FaRedo />
        </Button>
      </div>

      {loading && !previousData ? (
        <ClassicLoadingSpinner />
      ) : (
        <CustomTable
          error={error}
          hover
          striped
          bordered
          headers={headers}
          loading={loading}
          rows={containers.map((container) => (
            <tr key={container.id}>
              <td>
                <Link to={`/container/${container.id}`}>{container.name}</Link>
              </td>
              <td>
                <ContainerTypeDisplay
                  containerType={{
                    id: container.type_id,
                    imageUrl:
                      container.image_url || DEFAULT_CONTAINER_TYPE_ICON,
                    name: container.type_name,
                    hexCode: '',
                  }}
                  showContainerId={false}
                  size="s"
                />
              </td>
              <td>{container.status}</td>
              <td>
                {new Date(container.owner_since).toLocaleDateString(
                  i18n.language,
                  getDateFormatOptions(true),
                )}
              </td>
            </tr>
          ))}
        />
      )}

      {pageCount > 1 && (
        <CustomPagination
          pageCount={pageCount}
          currentPage={page}
          onPageChange={setPage}
        />
      )}
    </ContentSection>
  );
};

export default IndividualContainerStock;
